import { z } from "zod";
import { formStatusEnum } from "../db/schema";
import type { ActionResponse } from "./action";
import type { AvatarColor } from "./avatar";
import type { FormType } from "./common";
import {
  investigationParticipantSchema,
  participantSchema,
  photoSchema,
  timingSchema,
  videoSchema,
  witnessSchema,
} from "./common";

export const formStatusSchema = z
  .enum(formStatusEnum.enumValues)
  .default("in_progress");
export type FormStatus = z.infer<typeof formStatusSchema>;

// Schema for updating form status independently
export const updateFormStatusSchema = z.object({
  status: formStatusSchema,
});

///////////////////
// GENERAL
///////////////////
export const fieldTypeEnumSchema = z.enum(["radio", "dropdown", "text"]);

const baseFieldOptions = z.object({
  label: z.string(),
  isRequired: z.boolean(),
  acceptCorrections: z.boolean(),
  acceptActions: z.boolean(),

  // TODO: support
  // - Conditional fields
});

const radioOptionsSchema = z.object({
  type: z.literal(fieldTypeEnumSchema.enum.radio),
  payload: baseFieldOptions.extend({
    options: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
        actionRequired: z.boolean().default(false),
        correctionRequired: z.boolean().default(false),
      }),
    ),
    value: z.string().min(1).nullable(),
  }),
});

const dropdownOptionsSchema = z.object({
  type: z.literal(fieldTypeEnumSchema.enum.dropdown),
  payload: baseFieldOptions.extend({
    options: z.array(
      z.object({
        value: z.string(),
        label: z.string(),
        actionRequired: z.boolean().default(false),
        correctionRequired: z.boolean().default(false),
      }),
    ),
    value: z.string().min(1).nullable(),
  }),
});

const textOptionsSchema = z.object({
  type: z.literal(fieldTypeEnumSchema.enum.text),
  payload: baseFieldOptions.extend({
    placeholder: z.string().optional(),
    value: z.string().nullable(),
    options: z
      .array(
        z.object({
          value: z.string(),
          label: z.string(),
          actionRequired: z.boolean().default(false),
          correctionRequired: z.boolean().default(false),
        }),
      )
      .default([]),
  }),
});

// New work structure
export const fieldSchema = z.discriminatedUnion("type", [
  radioOptionsSchema,
  dropdownOptionsSchema,
  textOptionsSchema,
]);

export type FieldGeneric = z.infer<typeof fieldSchema>;

export type FormGeneralInfo = {
  id: string;
  viewId: string;
  type: FormType;
  status: FormStatus;
  createdAt: Date;
  updatedAt: Date;
  locationId: string;
  zoneId: string;
  createdByUser: {
    id: string;
    name: string;
    email: string;
    color: AvatarColor;
  };
  department: {
    id: string;
    name: string;
    color: AvatarColor;
  };
  actions: ActionResponse[];
};

// Define external schema to be used in the routes and client
// TODO: make a standard over
// - Optional
// - Empty array
// - Nullable fields

// TODO: add some refine validation based on fieldType
const safetyCheckCorrectionSchema = z.object({
  id: z.string(),
  value: z.string().min(1),
});

export const safetyCheckItemSchema = z.object({
  id: z.string().uuid(),
  label: z.string().min(1),
  field: fieldSchema,
  corrections: z.array(safetyCheckCorrectionSchema).default([]),
});

export type SafetyCheckItem = z.infer<typeof safetyCheckItemSchema>;

// TEMPORAL: only able to update value and corrections
// Actions will be updated in a separate endpoint
export const updateSafetyCheckItemSchema = z.object({
  id: z.string().uuid(),
  value: z.string().min(1).nullable(),
  corrections: z.array(safetyCheckCorrectionSchema),
});

///////////////////
// DRILL FORM
///////////////////
export const createDrillFormSchema = z.object({
  date: z.string().datetime(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),
  scenario: z.string().min(1),
});

export const updateDrillFormSchema = z.object({
  date: z.string().datetime(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),
  scenario: z.string().min(1),
  objective: z.string().optional(),
  development: z.string().optional(),
  participants: z.array(participantSchema).optional(),
  timings: z.array(timingSchema).optional(),
  photos: z.array(photoSchema).optional(),
  videos: z.array(videoSchema).optional(),
  insituCorrections: z.string().optional(),
  conclusion: z.string().optional(),
  conductorSignature: z.string().optional(),
  initiatorSignature: z.string().optional(),
});

///////////////////
// HSEQ VISIT FORM
///////////////////
// TODO: include it on frontend
export const createHseqVisitFormSchema = z.object({
  date: z.string().datetime(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),
});

export const updateHseqVisitFormSchema = z.object({
  date: z.string().datetime().optional(),
  locationId: z.string().min(1).optional(),
  zoneId: z.string().min(1).optional(),
  observer: z.string().optional(),
  participants: z.array(participantSchema).optional(),
  evaluatedProcedures: z.array(z.string()).optional(),
  strongPoints: z.string().optional(),
  insituCorrections: z.string().optional(),
  observations: z.string().optional(),
  // TODO: add refine to check the value is valid for the safety check
  // TODO: decide to include it always or not
  safetyChecks: z.array(safetyCheckItemSchema).optional(),
  photos: z.array(photoSchema).optional(),
  videos: z.array(videoSchema).optional(),
  observerSignature: z.string().optional(),
});

///////////////////
// MANAGEMENT FORM
///////////////////
export const createManagementFormSchema = z.object({
  date: z.string().datetime(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),
});

export const updateManagementFormSchema = z.object({
  date: z.string().datetime(),
  locationId: z
    .string()
    .min(1, { message: "Por favor seleccione una ubicación" })
    .optional(),
  zoneId: z
    .string()
    .min(1, { message: "Por favor seleccione una zona" })
    .optional(),

  changeInitiator: z.string().optional(),
  changeName: z.string().optional(),
  changeDescription: z.string().optional(),
  changeJustification: z.string().optional(),
  changeType: z.string().optional(),

  // TODO: add refine to check the value is valid for the safety check
  // TODO: decide to include it always or not
  safetyChecks: z.array(safetyCheckItemSchema).optional(),
  initiatorSignature: z.string().optional(),
});

export const managementFormSchema = z.object({
  departmentId: z.string().min(1),
  createdById: z.string().min(1),
  date: z.string().datetime(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),
  change_initiator: z.string().min(1),
  change_name: z.string().min(1),
  change_description: z.string().min(1),
  change_justification: z.string().min(1),
  change_type: z.string().min(1),
  safetyChecks: z.array(safetyCheckItemSchema),
  /*conductorSignature: z
    .string()
    .min(1, { message: "Conductor's signature is required" }),*/
});

///////////////////
// INVESTIGATION FORM
///////////////////
export const createInvestigationFormSchema = z.object({
  date: z.string().datetime(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),
});

export const updateInvestigationFormSchema = z.object({
  date: z.string().datetime(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),

  eventType: z.string().optional(),
  investigatedBy: z.string().optional(),
  detectedBy: z.string().optional(),
  assistedBy: z.string().optional(),
  taskBeingPerformed: z.string().optional(),
  equipmentOrSubstanceInvolved: z.string().optional(),
  eventDescription: z.string().optional(),
  participants: z.array(investigationParticipantSchema).optional(),
  witnesses: z.array(witnessSchema).optional(),
  investigationSignature: z.string().optional(),
  conductorSignature: z.string().optional(),
  preventionServiceSignature: z.string().optional(),

  // TODO: add refine to check the value is valid for the safety check
  // TODO: decide to include it always or not
  safetyChecks: z.array(safetyCheckItemSchema).optional(),
  photos: z.array(photoSchema).optional(),
  videos: z.array(videoSchema).optional(),
  insituCorrections: z.string().optional(),
  additionalComments: z.string().optional(),
  conclusion: z.string().optional(),
  closedAt: z.string().datetime().optional(),
});

///////////////////
// OBSERVATION FORM
///////////////////
export const createObservationFormSchema = z.object({
  date: z.string().datetime(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),
  workPosition: z
    .string()
    .min(1, { message: "Selecciona un puesto de trabajo" }),
});

export const updateObservationFormSchema = z.object({
  // Required
  date: z.string().datetime(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),
  workPosition: z.string().optional(),

  // Optional
  tasks: z.string().optional(),

  // Required conditional fields
  observationSequence: z.string().optional(),
  positiveActs: z.string().optional(),

  // Optional fields
  equipmentUsed: z.string().optional(),
  deviations: z.string().optional(),
  inSituCorrections: z.string().optional(),
  observationNotes: z.string().optional(),

  observationParticipants: z.array(participantSchema).optional(),
  observedParticipants: z.array(participantSchema).optional(),

  // TODO: add refine to check the value is valid for the safety check
  // TODO: decide to include it always or not
  safetyChecks: z.array(safetyCheckItemSchema).optional(),
  photos: z.array(photoSchema).optional(),
  videos: z.array(videoSchema).optional(),
  initiatorSignature: z.string().optional(),
});

///////////////////
// MAILBOX FORM
///////////////////
export const createMailboxFormSchema = z.object({
  date: z.string().datetime(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),
});

export const updateMailboxFormSchema = z.object({
  date: z.string().datetime().optional(),
  locationId: z.string().min(1).optional(),
  zoneId: z.string().min(1).optional(),
  announcementImportance: z.string().optional(),
  communicationType: z.string().optional(),
  description: z.string().optional(),
  immediateActions: z.string().optional(),
  possibleCauses: z.string().optional(),
  announcementAssessment: z.string().optional(),
  personalInjury: z.string().optional(),
  propertyDamage: z.string().optional(),
  environmentalDamage: z.string().optional(),
  imageDamage: z.string().optional(),
});

// Strict schema to validate completion when finalizing mailbox forms (DB-side types)
export const finalizeMailboxDbSchema = z.object({
  date: z.date(),
  locationId: z.string().min(1),
  zoneId: z.string().min(1),
  announcementImportance: z.string().min(1),
  communicationType: z.string().min(1),
  description: z.string().min(1),
  immediateActions: z.string().min(1),
  possibleCauses: z.string().min(1),
  announcementAssessment: z.string().min(1),
  personalInjury: z.string().min(1),
  propertyDamage: z.string().min(1),
  environmentalDamage: z.string().min(1),
  imageDamage: z.string().min(1),
});
